import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Popover, Stack, Typography } from '@mui/material';
import { useState } from 'react';

export interface CompanyListObject {
  id: string;
  slug: string;
  name: string;
}

interface SelectCompanyProps {
  selected: string;
  setSelected: (value: string) => void;
  list: Array<CompanyListObject>;
  isLoading: boolean;
  error: unknown;
}

interface ResetButtonForPopoverProps {
  resetOptionMethod: () => void;
}

export const getComponyNameById = (
  id: string,
  list: CompanyListObject[],
): string | undefined => {
  if (Array.isArray(list)) {
    return list?.find((item) => item.id === id)?.name;
  }
  return undefined;
};

const ResetButtonForPopover = ({
  resetOptionMethod,
}: ResetButtonForPopoverProps) => (
  <Button
    onClick={(event) => {
      resetOptionMethod();
      event.stopPropagation();
    }}
    sx={{
      marginLeft: '8px',
      marginRight: '5px',
      padding: 0,
      minWidth: 0,
    }}
  >
    <CloseIcon
      sx={{
        padding: 0,
        fontSize: 'small',
        stroke: '#000000',
        strokeWidth: '1.5px',
      }}
    />
  </Button>
);

const SelectCompany = ({
  selected,
  setSelected,
  list,
  isLoading,
  error,
}: SelectCompanyProps) => {
  const [departmentAnchorEl, setDepartmentAnchorEl] =
    useState<null | HTMLElement>(null);
  const departmentOpen = Boolean(departmentAnchorEl);

  const handleDepartmentClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setDepartmentAnchorEl(event.currentTarget);
  };

  const handleDepartmentClose = () => {
    setDepartmentAnchorEl(null);
  };

  const resetDepartmentOption = () => {
    setSelected('');
  };

  return (
    <Stack>
      <Button
        variant='outlined'
        style={{
          borderRadius: '4px',
          border: '1px solid #878787',
          backgroundColor: 'white',
          padding: '13px 12px',
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          margin: '0px 4px 0px 4px',
        }}
        disabled={isLoading || error != undefined}
        onClick={handleDepartmentClick}
        endIcon={
          !getComponyNameById(selected, list) ? (
            <ExpandMoreIcon
              style={{
                color: '#878787',
                marginLeft: '8px',
                padding: '0',
                transform: 'rotate(0deg)',
              }}
              fontSize='small'
            />
          ) : (
            <ResetButtonForPopover resetOptionMethod={resetDepartmentOption} />
          )
        }
      >
        <Typography
          sx={{
            color: 'var(--grey-white-background, #878787)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            paddingRight: '10px',
            textTransform: 'none',
          }}
        >
          Company:
        </Typography>
        <Typography
          sx={{
            color: 'var(--Black, #181818)',
            fontFamily: 'Schibsted Grotesk',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            textTransform: 'none',
          }}
        >
          {selected !== '' ? getComponyNameById(selected, list) : 'All'}
        </Typography>
      </Button>

      <Popover
        sx={{ marginTop: '5px' }}
        open={departmentOpen}
        anchorEl={departmentAnchorEl}
        onClose={handleDepartmentClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '246px',
            maxHeight: '400px',
            overflow: 'auto',
            borderRadius: '6px',
            backgroundColor: '#FFF',
          }}
        >
          {list?.map((item) => (
            <Button
              sx={{
                width: '100%',
                margin: '0',
                color: '#000',
                fontFamily: 'Schibsted Grotesk',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                textTransform: 'none',
                transition: 'background-color 0.3s',
                p: '7px 17px 7px 17px',
                textAlign: 'left',
                justifyContent: 'flex-start',
                '&:hover': {
                  backgroundColor: 'rgba(0, 206, 207, 0.3)',
                },
              }}
              key={item.id}
              onClick={() => setSelected(item.id)}
            >
              {item.name}
            </Button>
          ))}
        </div>
      </Popover>
    </Stack>
  );
};

export default SelectCompany;
